import { getI18n } from 'react-i18next';
import { endOfDay, endOfMonth, format, lastDayOfMonth, startOfMonth, sub } from 'date-fns';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { RentsListSortType } from '@/lib/coloc-api/admin';
import { useColocApi } from '@/hooks/use-coloc-api';
import { RentStatus } from '@/features/admin/rents/types';

const i18n = getI18n();

type ListRentStatusOption = {
  status: RentStatus;
  displayValue: string;
};

const listRentStatus: Array<ListRentStatusOption> = [
  {
    status: 'due',
    displayValue: i18n.t('js.features.admin.rents.rent_list.status_options.due'),
  },
  {
    status: 'in_flight',
    displayValue: i18n.t('js.features.admin.rents.rent_list.status_options.in_flight'),
  },
  {
    status: 'partially_collected',
    displayValue: i18n.t('js.features.admin.rents.rent_list.status_options.partially_collected'),
  },
  {
    status: 'collected',
    displayValue: i18n.t('js.features.admin.rents.rent_list.status_options.collected'),
  },
  {
    status: 'overflow',
    displayValue: i18n.t('js.features.admin.rents.rent_list.status_options.overflow'),
  },
  {
    status: 'too_late',
    displayValue: i18n.t('js.features.admin.rents.rent_list.status_options.too_late'),
  },
  {
    status: 'failed',
    displayValue: i18n.t('js.features.admin.rents.rent_list.status_options.failed'),
  },
  {
    status: 'pending_payout',
    displayValue: i18n.t('js.features.admin.rents.rent_list.status_options.pending_payout'),
  },
  {
    status: 'resolved',
    displayValue: i18n.t('js.features.admin.rents.rent_list.status_options.resolved'),
  },
  {
    status: 'ignored',
    displayValue: i18n.t('js.features.admin.rents.rent_list.status_options.ignored'),
  },
];

type ListRentReminderOption = {
  reminders_sent: number;
  displayValue: string;
};

const listRentReminders: Array<ListRentReminderOption> = [
  {
    reminders_sent: 0,
    displayValue: i18n.t('js.features.admin.rents.rent_list.reminder_options.0'),
  },
  {
    reminders_sent: 1,
    displayValue: i18n.t('js.features.admin.rents.rent_list.reminder_options.1'),
  },
  {
    reminders_sent: 2,
    displayValue: i18n.t('js.features.admin.rents.rent_list.reminder_options.2'),
  },
  {
    reminders_sent: 3,
    displayValue: i18n.t('js.features.admin.rents.rent_list.reminder_options.3'),
  },
];

const useRentListFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize states from URL params or defaults
  const [selectedListRentStatus, setSelectedListRentStatus] = useState<
    ListRentStatusOption | undefined
  >(() => {
    const status = searchParams.get('status');
    if (status) {
      return listRentStatus.find((rentStatus) => rentStatus.status === (status as RentStatus));
    }
    return undefined;
  });

  const [selectedListRentReminder, setSelectedListRentReminder] =
    useState<ListRentReminderOption | null>(() => {
      const reminders = searchParams.get('reminders_sent');
      if (reminders !== null) {
        const remindersNum = parseInt(reminders, 10);
        return (
          listRentReminders.find((reminder) => reminder.reminders_sent === remindersNum) ?? null
        );
      }
      return null;
    });

  const [rentStatusQuery, setRentStatusQuery] = useState('');
  const [rentReminderQuery, setRentReminderQuery] = useState('');

  const [startDate, setStartDate] = useState<Date | undefined>(() => {
    const startDateQuery = searchParams.get('start_date');
    if (startDateQuery) {
      return startOfMonth(new Date(startDateQuery));
    }
    return sub(new Date(format(new Date(), 'yyyy-MM-01')), { months: 0 });
  });

  const [endDate, setEndDate] = useState<Date | undefined>(() => {
    const endDateQuery = searchParams.get('end_date');
    const contractId = searchParams.get('contract_id');
    if (endDateQuery) {
      return endOfMonth(endOfDay(new Date(endDateQuery)));
    }
    if (contractId) {
      return undefined;
    }
    return endOfDay(lastDayOfMonth(sub(new Date(format(new Date(), 'yyyy-MM-01')), { months: 0 })));
  });

  const [searchQuery, setSearchQuery] = useState<string | undefined>(
    searchParams.get('tenant_name') ?? undefined
  );

  const { setColumnToSort, sortDirection, selectedColumnToSort } =
    useColocApi<RentsListSortType>()!;

  const [selectedHouse, setSelectedHouse] = useState<{ id: string; name: string } | undefined>(
    () => {
      const colocationId = searchParams.get('colocation_id');
      const houseId = searchParams.get('house_id');
      const houseName = searchParams.get('house_name');

      if (colocationId) {
        return { id: colocationId, name: '' };
      }

      if (houseId && houseName) {
        return { id: houseId, name: houseName };
      }

      return undefined;
    }
  );

  const [selectedContractId, setSelectedContractId] = useState<string | undefined>(
    searchParams.get('contract_id') ?? undefined
  );
  const [housesQuery, setHousesQuery] = useState('');

  // Set default sorting to house name
  useEffect(() => {
    setColumnToSort('house_name');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync filter state to URL whenever it changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    if (searchQuery) {
      params.set('tenant_name', searchQuery);
    } else {
      params.delete('tenant_name');
    }

    if (startDate) {
      params.set('start_date', format(startDate, 'yyyy-MM-dd'));
    } else {
      params.delete('start_date');
    }

    if (endDate) {
      params.set('end_date', format(endDate, 'yyyy-MM-dd'));
    } else {
      params.delete('end_date');
    }

    if (selectedListRentStatus) {
      params.set('status', selectedListRentStatus.status);
    } else {
      params.delete('status');
    }

    if (selectedHouse) {
      params.set('colocation_id', selectedHouse.id);
    } else {
      params.delete('colocation_id');
    }

    params.delete('house_id');
    params.delete('house_name');

    if (selectedListRentReminder !== null) {
      params.set('reminders_sent', String(selectedListRentReminder.reminders_sent));
    } else {
      params.delete('reminders_sent');
    }

    if (selectedContractId) {
      params.set('contract_id', selectedContractId);
    } else {
      params.delete('contract_id');
    }

    setSearchParams(params, { replace: true });
  }, [
    searchQuery,
    startDate,
    endDate,
    selectedListRentStatus,
    selectedHouse,
    selectedListRentReminder,
    selectedContractId,
    searchParams,
    setSearchParams,
  ]);

  const setDateRange = useCallback((new_date_range: [Date | null, Date | null]) => {
    setStartDate(new_date_range[0] ?? undefined);
    setEndDate(new_date_range[1] ?? undefined);
  }, []);

  const filteredListRentsStatusArray = useMemo(() => {
    return listRentStatus.filter((statusObject) =>
      statusObject.displayValue.includes(rentStatusQuery)
    );
  }, [rentStatusQuery]);

  const filteredListRentsReminderArray = useMemo(
    () =>
      listRentReminders.filter((reminderObj) =>
        reminderObj.displayValue.includes(rentReminderQuery)
      ),
    [rentReminderQuery]
  );

  const setSelectedListRentStatusByValue = useCallback((status: RentStatus) => {
    const statusOption = listRentStatus.find((rentStatus) => rentStatus.status === status);

    setSelectedListRentStatus(statusOption);
  }, []);
  return {
    selectedListRentStatus,
    setSelectedListRentStatus,
    setSelectedListRentStatusByValue,
    selectedListRentReminder,
    setSelectedListRentReminder,
    setRentStatusQuery,
    rentStatusQuery,
    rentReminderQuery,
    setRentReminderQuery,
    filteredListRentsStatusArray,
    filteredListRentsReminderArray,
    startDate,
    endDate,
    setDateRange,
    searchQuery,
    setSearchQuery,
    setColumnToSort,
    sortDirection,
    selectedColumnToSort,
    selectedHouse,
    setSelectedHouse,
    selectedContractId,
    setSelectedContractId,
    housesQuery,
    setHousesQuery,
  };
};

const GetRentsListFiltersContext = createContext<ReturnType<typeof useRentListFilters> | undefined>(
  undefined
);

const GetRentsListFiltersContextProvider = ({ children }: { children: React.ReactNode }) => {
  const hook = useRentListFilters();

  return (
    <GetRentsListFiltersContext.Provider value={hook}>
      {children}
    </GetRentsListFiltersContext.Provider>
  );
};

const useRentsListFiltersContext = () => {
  const context = useContext(GetRentsListFiltersContext);

  if (context === null) {
    throw new Error(
      'useRentsListFiltersContext must be used within a GetRentsListFiltersContextProvider'
    );
  }

  return context;
};

export { GetRentsListFiltersContextProvider, useRentsListFiltersContext };
